import { useState } from 'react';
import { toast } from 'sonner';
import { Check, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { useAppStore } from '../../store/appStore';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

/** The shared page's address: this app's own origin, whatever the API's is. */
export const shareUrl = (token) => `${window.location.origin}/treatment/${token}`;

/**
 * Share a treatment by link. Sharing is off until switched on, and each change
 * here is saved at once - on its own, so unsaved edits in the editor are left
 * alone. `onShareChange` receives the share fields to merge into the page.
 */
export default function TreatmentShareDialog({ open, onOpenChange, treatment, onShareChange }) {
  const { updateTreatment, resetTreatmentShareLink } = useAppStore();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const link = shareUrl(treatment.share_token);

  const apply = async (fn) => {
    setBusy(true);
    try {
      const updated = await fn();
      onShareChange({
        share_enabled: updated.share_enabled,
        share_equipment: updated.share_equipment,
        share_token: updated.share_token,
      });
      return true;
    } catch (e) {
      toast.error('Could not change sharing');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy the link');
    }
  };

  const reset = async () => {
    if (!window.confirm('Make a new link? The current one stops working for anyone who has it.')) return;
    if (await apply(() => resetTreatmentShareLink(treatment.id))) toast.success('New link made');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share shot list</DialogTitle>
          <DialogDescription>
            Anyone with the link sees the saved shot list, read-only, and can download it as a PDF. No account needed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="share-enabled">Anyone with the link can view</Label>
            <Switch
              id="share-enabled"
              checked={!!treatment.share_enabled}
              disabled={busy}
              onCheckedChange={(v) => apply(() => updateTreatment(treatment.id, { share_enabled: v }))}
              data-testid="share-enabled-switch"
            />
          </div>

          {treatment.share_enabled && (
            <>
              <div className="flex gap-2">
                <Input readOnly value={link} aria-label="Share link" onFocus={(e) => e.target.select()} data-testid="share-link" />
                <Button variant="outline" onClick={copy} aria-label="Copy link">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button variant="outline" asChild aria-label="Open the shared page">
                  <a href={link} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4" /></a>
                </Button>
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="share-equipment">Show equipment</Label>
                  <p className="text-xs text-muted-foreground">Item names only. Suppliers and notes are never shown.</p>
                </div>
                <Switch
                  id="share-equipment"
                  checked={!!treatment.share_equipment}
                  disabled={busy}
                  onCheckedChange={(v) => apply(() => updateTreatment(treatment.id, { share_equipment: v }))}
                />
              </div>

              <div className="flex items-center justify-between gap-4 pt-2 border-t border-line">
                <p className="text-xs text-muted-foreground">Sent it to the wrong person? A new link stops the old one.</p>
                <Button variant="outline" size="sm" onClick={reset} disabled={busy}>
                  {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                  Reset link
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
